import { SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import {
  json,
  Link,
  MetaFunction,
  useLoaderData,
  useNavigate,
  useSearchParams,
} from "@remix-run/react";
import {
  Accordion,
  AccordionItem,
  Button,
  Card,
  Chip,
  Select,
  Selection,
  SelectItem,
  Skeleton,
} from "@heroui/react";
import { MdKeyboardDoubleArrowRight } from "react-icons/md";
import { ProductCard } from "app/components/Product/ProductCard";
import { searchProductCatalogClient } from "app/api/product/search-product-catalog.client";
import { getTypesenseNlModelId } from "app/api/typesense/typesense-browser-config";
import TablePagination from "app/components/TablePagination";
import { useQuery } from "@tanstack/react-query";
import { ProductObject } from "app/api/product/product.type";
import { BiFilter } from "react-icons/bi";
import { storageKey } from "app/utils/storageKeys";
import { parse } from "cookie";
import { setPageLimit } from "app/utils/fn";
import { getSession } from "app/auth/sessions";
import { useAtom } from "jotai";
import { sortOrder } from "app/atoms/utils.atom";
import { sortOptions } from "app/utils/sortOptions";
import { SEOHandle } from "@nasa-gcn/remix-seo";

export const handle: SEOHandle = {
  getSitemapEntries: async () => {
    return [];
  },
};

export const meta: MetaFunction<typeof loader> = ({ data, location }) => {
  const q = data?.q || "";
  const page = new URLSearchParams(location.search).get("page") || "1";
  const title = q
    ? `Search results for '${q}'${
        page !== "1" ? ` - Page ${page}` : ""
      } | Search Custom Promotional Products Now`
    : `Search | Search Custom Promotional Products Now`;
  const description = q
    ? `Browse custom promotional products matching '${q}'. Perfect for events, giveaways, and corporate branding.`
    : `Search custom products perfect for events, giveaways, and corporate branding`;
  return [
    { title },
    { name: "description", content: description },
    { name: "og:description", content: description },
    {
      tagName: "link",
      rel: "canonical",
      href: `${process.env.PUBLIC_URL || ""}/search${q ? `?q=${encodeURIComponent(q)}` : ""}`,
    },
  ];
};

export async function loader({ request }: { request: Request }) {
  const session = await getSession(request.headers.get("Cookie"));
  const url = new URL(request.url);
  const cookies = parse(request.headers.get("Cookie") || "");
  const q = url.searchParams.get("q") || "";
  const colours = url.searchParams.getAll("colours");
  const page = parseInt(url.searchParams.get("page") || "1", 10);
  const limit = parseInt(url.searchParams.get("limit") || cookies.pageLimit || "16", 10);
  const minPriceParam = url.searchParams.get("minPrice");
  const maxPriceParam = url.searchParams.get("maxPrice");
  const minQty = parseInt(url.searchParams.get("minQty") || "0", 10);
  const sort = url.searchParams.get("sort") || cookies.sortOrder || "lowest-price";

  return json(
    {
      q,
      page,
      limit,
      sort,
      colours,
      ...(minQty > 0 ? { minQty: String(minQty) } : {}),
      ...(minPriceParam && !Number.isNaN(parseFloat(minPriceParam))
        ? { minPrice: minPriceParam }
        : {}),
      ...(maxPriceParam && !Number.isNaN(parseFloat(maxPriceParam))
        ? { maxPrice: maxPriceParam }
        : {}),
      userType: session.get("userType") || undefined,
    },
    {
      headers: {
        "Cache-Control": "no-store",
      },
    },
  );
}

const SearchPage = () => {
  const loaderData = useLoaderData<typeof loader>();
  const [currentSortOrder, setCurrentSortOrder] = useAtom(sortOrder);
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(loaderData.page || 1);
  const [limit, setLimit] = useState(loaderData.limit || 16);

  const {
    data: searchData,
    isLoading: searchLoading,
    isError: searchPageError,
    refetch: refetchSearchPage,
  } = useQuery({
    queryKey: ["search-page-catalog", loaderData],
    queryFn: () => {
      const nlModelId = getTypesenseNlModelId();
      return searchProductCatalogClient({
        page: loaderData.page,
        limit: loaderData.limit,
        sort: loaderData.sort,
        ...(nlModelId ? { nlQuery: true as const, nlModelId } : {}),
        search: loaderData.q,
        colours: loaderData.colours?.length ? loaderData.colours : undefined,
        minQty: loaderData.minQty,
        minPrice: loaderData.minPrice,
        maxPrice: loaderData.maxPrice,
        userType: loaderData.userType,
      });
    },
  });
  const [searchParams] = useSearchParams();

  // Store query parameters in state
  const [queryValue, setQueryValue] = useState<string>(searchParams.get("q") ?? "");
  const [minPrice, setMinPrice] = useState<string>(searchParams.get("minPrice") ?? "");
  const [minQty, setMinQty] = useState<string>(searchParams.get("minQty") ?? "");

  const [maxPrice, setMaxPrice] = useState<string>(searchParams.get("maxPrice") ?? "");
  const [colours, setColours] = useState<string[]>(searchParams.getAll("colours") ?? []);

  // Update state when searchParams change
  useEffect(() => {
    setQueryValue(searchParams.get("q") ?? "");
    if (searchParams.get("sort")) {
      setCurrentSortOrder(searchParams.get("sort") ?? "");
    }
    setMinQty(searchParams.get("minQty") ?? "");

    setMinPrice(searchParams.get("minPrice") ?? "");
    setMaxPrice(searchParams.get("maxPrice") ?? "");
    setColours(searchParams.getAll("colours") ?? []);
  }, [searchParams, setCurrentSortOrder]);

  const filteredProducts = useMemo(() => {
    if (!searchData?.products) {
      return {
        totalPages: 0,
        hasPrevious: false,
        hasNext: false,
        nextPage: 0,
        prevPage: 0,
        total: 0,
        limit: loaderData.limit || 0,
        products: [] as ProductObject[],
        sort: loaderData.sort,
      };
    }

    const {
      products,
      pagination: { page, limit, totalPages, nextPage, prevPage, hasNextPage, hasPrevPage, total },
    } = searchData;
    return {
      page,
      totalPages,
      hasPrevious: hasPrevPage,
      hasNext: hasNextPage,
      nextPage: nextPage || 0,
      prevPage: prevPage || 0,
      limit,
      total,
      products,
      sort: loaderData.sort,
    };
  }, [searchData, loaderData.limit, loaderData.sort]);

  const updateQueryParams = useCallback(
    ({ page, limit, sort }: { page: number; limit: number; sort?: string }) => {
      let queryString = `?page=${page}&limit=${limit}`;
      if (queryValue) queryString += `&q=${queryValue}`;
      if (currentSortOrder) queryString += `&sort=${sort}`;
      if (minQty) queryString += `&minQty=${minQty}`;

      if (minPrice) queryString += `&minPrice=${minPrice}`;
      if (maxPrice) queryString += `&maxPrice=${maxPrice}`;

      // Append multiple colours
      if (colours && colours.length > 0) {
        queryString += `&colours=${colours.join("&colours=")}`;
      }
      navigate(queryString);
    },
    [navigate, queryValue, minQty, minPrice, maxPrice, colours, currentSortOrder],
  );

  const handleNext = useCallback(
    (pageNumber: number) => {
      if (filteredProducts?.hasNext && !pageNumber) {
        updateQueryParams({
          page: filteredProducts.nextPage,
          limit,
          sort: currentSortOrder,
        });
        setCurrentPage(filteredProducts?.nextPage);
      } else {
        updateQueryParams({
          page: pageNumber,
          limit,
          sort: currentSortOrder,
        });
        setCurrentPage(pageNumber);
      }
    },
    [
      filteredProducts.hasNext,
      filteredProducts.nextPage,
      limit,
      currentSortOrder,
      updateQueryParams,
    ],
  );

  const handlePrevious = useCallback(() => {
    if (filteredProducts.hasPrevious) {
      updateQueryParams({
        page: filteredProducts.prevPage,
        limit,
        sort: currentSortOrder,
      });
      setCurrentPage(filteredProducts.prevPage);
    }
  }, [
    filteredProducts.hasPrevious,
    filteredProducts.prevPage,
    limit,
    currentSortOrder,
    updateQueryParams,
  ]);

  const handleChangeLimit = useCallback(
    (newLimit: SetStateAction<number>) => {
      setLimit(newLimit);
      setPageLimit(newLimit as number);
      updateQueryParams({
        page: currentPage,
        limit: newLimit as number,
        sort: currentSortOrder,
      });
      setCurrentPage(1);
    },
    [currentPage, currentSortOrder, updateQueryParams],
  );

  const handleSortChange = useCallback(
    (keys: Selection) => {
      const newSort = Array.from(keys).join(",");
      setCurrentSortOrder(newSort);
      updateQueryParams({
        page: currentPage,
        limit,
        sort: newSort,
      });
    },
    [currentPage, limit, updateQueryParams, setCurrentSortOrder],
  );

  const removeColour = useCallback(
    (col: string) => {
      const next = colours.filter((c) => c !== col);
      let queryString = `?page=${currentPage}&limit=${limit}`;
      if (queryValue) queryString += `&q=${queryValue}`;
      if (currentSortOrder) queryString += `&sort=${currentSortOrder}`;
      if (minQty) queryString += `&minQty=${minQty}`;
      if (minPrice) queryString += `&minPrice=${minPrice}`;
      if (maxPrice) queryString += `&maxPrice=${maxPrice}`;
      if (next.length > 0) {
        queryString += `&colours=${next.join("&colours=")}`;
      }
      navigate(queryString);
    },
    [
      navigate,
      currentPage,
      limit,
      queryValue,
      currentSortOrder,
      minQty,
      minPrice,
      maxPrice,
      colours,
    ],
  );

  const clearMinPrice = useCallback(() => {
    updateQueryParams({ page: currentPage, limit, sort: currentSortOrder });
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("minPrice");
    navigate(`?${newParams.toString()}`);
  }, [currentPage, limit, currentSortOrder, searchParams, navigate]);

  const clearMaxPrice = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("maxPrice");
    navigate(`?${newParams.toString()}`);
  }, [searchParams, navigate]);

  const clearMinQty = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("minQty");
    navigate(`?${newParams.toString()}`);
  }, [searchParams, navigate]);

  const clearSort = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("sort");
    navigate(`?${newParams.toString()}`);
  }, [searchParams, navigate]);

  useEffect(() => {
    if (localStorage) {
      const _limit = localStorage.getItem(storageKey.pageLimit) || "16";
      setLimit(Number(_limit));
    }
  }, []);

  useEffect(() => {
    setCurrentPage(loaderData.page);
  }, [loaderData.page]);

  useEffect(() => {
    setLimit(loaderData.limit);
  }, [loaderData.limit]);

  // --- SEO: Structured Data ---
  const productsForLD = filteredProducts.products.map((item: ProductObject) => ({
    "@type": "Product",
    name: item?.overview?.name,
    image: item?.overview?.heroImage,
    description: item?.product?.description,
    sku: item?.overview?.code,
    offers: {
      "@type": "Offer",
      priceCurrency: "AUD",
      price: item?.price?.min,
      availability: "http://schema.org/InStock",
      url: `${process.env.PUBLIC_URL || "https://www.promotionalproductsnow.au"}/category/${
        item?.category?.name ? item?.category?.name.replace(/\s+/g, "-").toLowerCase() : "_"
      }${
        item?.subCategory?.name
          ? "/" + item?.subCategory?.name.replace(/\s+/g, "-").toLowerCase()
          : ""
      }/${item?.slug}`,
    },
  }));
  const itemListLD = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: productsForLD.map((p: any, i: any) => ({
      "@type": "ListItem",
      position: i + 1,
      item: p,
    })),
  };
  // --- END Structured Data ---

  return (
    <div className="space-y-6">
      {/* SEO: Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListLD) }}
      />
      <div className="w-full">
        <div className="flex flex-row py-3 px-5 md:px-0 md:pb-3 md:py-0">
          <div className="flex flex-row items-center w-full">
            <Link to="/">
              <span className="text-sm md:text-base text-primary">Home</span>
            </Link>
            <MdKeyboardDoubleArrowRight size={18} className="text-gray dark:text-foreground" />
            <span className="text-sm md:text-base text-primary capitalize">{queryValue}</span>
          </div>
        </div>
      </div>

      <div className="space-y-4 md:space-y-8">
        <div className="px-5 md:px-0">
          <div className="flex items-center justify-start">
            {queryValue ? (
              <h1 className="text-xl md:text-2xl lg:text-4xl xl:text-6xl font-bold capitalize">
                Search Results {queryValue && `for ${queryValue}`}
              </h1>
            ) : !queryValue && currentSortOrder ? (
              <h1 className="text-xl md:text-2xl lg:text-4xl xl:text-6xl font-bold capitalize">
                {currentSortOrder}
              </h1>
            ) : (
              <h1 className="text-xl md:text-2xl lg:text-4xl xl:text-6xl font-bold capitalize">
                Results
              </h1>
            )}
          </div>
          {filteredProducts &&
          filteredProducts?.total &&
          Number(filteredProducts?.total || 0) !== 0 ? (
            <div className="italic text-lg text-foreground-800 mb-6">
              ({filteredProducts?.total}) Products
            </div>
          ) : null}
        </div>

        <div className="z-30 px-4 md:px-0 py-2 -mt-2 mb-2 bg-zinc-50/95 dark:bg-zinc-900/95 backdrop-blur-md border-b border-default-200 dark:border-default-100">
          <Accordion className="bg-zinc-50 dark:bg-zinc-800 rounded-xl shadow-sm">
            <AccordionItem
              key="1"
              aria-label="Sort"
              subtitle="Press to expand"
              title={
                <div className="flex space-x-3 items-center">
                  <BiFilter size={24} className="text-primary" />
                  <span className="text-foreground md:text-2xl font-semibold text-center">
                    Sort
                  </span>
                </div>
              }
            >
              <div className="justify-end w-full md:w-2/5">
                <div className="flex items-end justify-between space-x-3 mb-2 w-full">
                  <Select
                    variant="bordered"
                    label="Sort by: Price"
                    labelPlacement="inside"
                    className="max-w-xs text-center"
                    placeholder=""
                    classNames={{
                      trigger: ["border-zinc-100 rounded-md py-1"],
                      label: ["text-sm w-28"],
                      popoverContent: "w-64",
                    }}
                    items={sortOptions}
                    size="sm"
                    selectedKeys={[currentSortOrder]}
                    onSelectionChange={handleSortChange}
                  >
                    {(option) => <SelectItem key={option.key}>{option.label}</SelectItem>}
                  </Select>
                </div>
              </div>
            </AccordionItem>
          </Accordion>

          {(colours.length > 0 || minPrice || maxPrice || minQty || currentSortOrder) && (
            <div className="flex flex-wrap gap-1.5 mt-2 px-1" aria-label="Active search filters">
              <span className="text-[10px] font-bold text-foreground-400 uppercase mr-1 flex items-center">
                Filters:
              </span>
              {colours.map((c) => (
                <Chip
                  key={c}
                  size="sm"
                  variant="flat"
                  style={{ backgroundColor: c }}
                  classNames={{ base: "border border-default-200 min-w-8 h-6", content: "sr-only" }}
                  onClose={() => removeColour(c)}
                  aria-label={`Remove colour filter ${c}`}
                >
                  .
                </Chip>
              ))}
              {minPrice ? (
                <Chip
                  size="sm"
                  variant="flat"
                  color="primary"
                  className="h-6"
                  onClose={clearMinPrice}
                >
                  Min ${minPrice}
                </Chip>
              ) : null}
              {maxPrice ? (
                <Chip
                  size="sm"
                  variant="flat"
                  color="primary"
                  className="h-6"
                  onClose={clearMaxPrice}
                >
                  Max ${maxPrice}
                </Chip>
              ) : null}
              {minQty ? (
                <Chip
                  size="sm"
                  variant="flat"
                  color="secondary"
                  className="h-6"
                  onClose={clearMinQty}
                >
                  Qty ≥ {minQty}
                </Chip>
              ) : null}
              {currentSortOrder && currentSortOrder !== "lowest-price" ? (
                <Chip size="sm" variant="flat" color="default" className="h-6" onClose={clearSort}>
                  {sortOptions.find((o) => o.key === currentSortOrder)?.label || currentSortOrder}
                </Chip>
              ) : null}
            </div>
          )}
        </div>

        {searchPageError && (
          <div
            className="mx-4 md:mx-0 mb-4 p-4 rounded-xl border border-danger-200 bg-danger-50 dark:bg-danger-900/20 dark:border-danger-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
            role="alert"
          >
            <p className="text-danger-700 dark:text-danger-300 text-sm">
              We couldn&apos;t load these results. Check your connection and try again.
            </p>
            <Button
              size="sm"
              color="danger"
              variant="flat"
              onPress={() => refetchSearchPage()}
              className="shrink-0"
            >
              Retry
            </Button>
          </div>
        )}

        {searchLoading ? (
          <div className="flex flex-col gap-10 md:px-20 px-5 w-full">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((v) => (
                <Card
                  key={v}
                  className="w-full h-[400px] p-0 shadow-sm border border-default-100"
                  radius="lg"
                >
                  <Skeleton className="h-48 w-full rounded-t-lg">
                    <div className="h-48 rounded-t-lg bg-default-300" />
                  </Skeleton>
                  <div className="p-4 space-y-4">
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-3/4 rounded-lg">
                        <div className="h-4 w-3/4 rounded-lg bg-default-200" />
                      </Skeleton>
                      <Skeleton className="h-3 w-1/2 rounded-lg">
                        <div className="h-3 w-1/2 rounded-lg bg-default-200" />
                      </Skeleton>
                    </div>
                    <div className="pt-4 space-y-2">
                      <Skeleton className="h-3 w-full rounded-lg">
                        <div className="h-3 w-full rounded-lg bg-default-200" />
                      </Skeleton>
                      <Skeleton className="h-3 w-4/5 rounded-lg">
                        <div className="h-3 w-4/5 rounded-lg bg-default-200" />
                      </Skeleton>
                    </div>
                    <div className="pt-4">
                      <Skeleton className="h-10 w-full rounded-lg">
                        <div className="h-10 w-full rounded-lg bg-default-300" />
                      </Skeleton>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ) : searchPageError ? null : filteredProducts.products.length === 0 ? (
          <div className="text-center py-12 text-xl">No products found matching your criteria</div>
        ) : (
          <div className="flex flex-col gap-10 md:px-20 px-5 w-full">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {filteredProducts.products.map((item: ProductObject) => (
                <ProductCard
                  key={item?._id}
                  isSpecialSupplier={item?.supplier?.isSpecialSupplier ?? false}
                  labels={item?.labels || []}
                  image={item?.overview?.heroImage}
                  images={item?.product?.images}
                  title={item?.overview?.name}
                  productCode={item?.overview?.code}
                  description={item?.product?.description}
                  userTypePrice={item?.userTypePrice}
                  qty={item?.quantity}
                  slug={item?.slug}
                  category={
                    item?.category?.name ||
                    item?.globalProductCategory?.name ||
                    item?.product?.categorisation?.productType?.typeName
                  }
                  subCatgory={
                    item?.subCategory?.name ||
                    item?.globalProductSubCategory?.name ||
                    item?.product?.categorisation?.supplierSubCategory
                  }
                />
              ))}
            </div>

            <div className="flex w-full justify-center px-5 pb-1">
              <TablePagination
                totalPages={filteredProducts.totalPages}
                currentPage={filteredProducts.page ?? 1}
                handlePrevious={handlePrevious}
                handleNext={(p) => handleNext(p as number)}
                setLimit={handleChangeLimit}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
